/**
 * ConversationOutcome — how a closed conversation ended, and the one re-route it offers.
 *
 * The re-route is the closed table of IMP-BR-16, by outcome: defect escalated -> to preference;
 * preference applied, explained and declined (not changeable) -> to defect; abandoned -> none.
 */

import { Link } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';
import type { ImprovementRerouteBody } from '@resida/api-types/agent';
import { Button } from '../../ui/button';
import type {
  ImprovementConversationView,
  ImprovementOutcome,
} from '../../../api/improvement-conversations.api';
import { agentName, proposalRuleText, scopePhrase } from '../../../lib/playbook';
import type { ConfirmedChange } from './useImprovementConversation';

const REROUTE_TARGET: Record<ImprovementOutcome, ImprovementRerouteBody['to'] | null> = {
  defect_escalated: 'preference',
  preference_applied: 'defect',
  explained: 'defect',
  declined: 'defect',
  abandoned: null,
};

const REROUTE_LABEL: Record<ImprovementRerouteBody['to'], string> = {
  preference: "Actually, this is how I want it to work",
  defect: 'This was a mistake by the agent, not how I want it to work',
};

export interface ConversationOutcomeProps {
  view: ImprovementConversationView;
  outcome: ImprovementOutcome;
  confirmed: ConfirmedChange | null;
  propertyName: (propertyId: string) => string | undefined;
  busy: boolean;
  onReroute: (to: ImprovementRerouteBody['to']) => void;
  onDone: () => void;
}

export function ConversationOutcome({ view, outcome, confirmed, propertyName, busy, onReroute, onDone }: ConversationOutcomeProps) {
  const noun = agentName(view.anchor.agentKind).toLowerCase();
  const lastAgentText = [...view.turns].reverse().find((t) => t.role === 'agent')?.text ?? '';
  const target = REROUTE_TARGET[outcome];
  const rerouteButton = target && (
    <button
      type="button"
      disabled={busy}
      onClick={() => onReroute(target)}
      data-testid="improvement-reroute"
      className="flex min-h-[44px] items-center text-left text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-50"
    >
      {REROUTE_LABEL[target]}
    </button>
  );

  return (
    <section className="flex flex-col gap-3" data-testid={`improvement-outcome-${outcome}`} aria-label="Conversation outcome">
      {outcome === 'preference_applied' && (
        <>
          <div
            role="status"
            data-testid="improvement-outcome-banner"
            className="flex gap-2 rounded-lg border border-success/25 bg-success/10 p-3 text-sm text-success"
          >
            <Check className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden="true" />
            <span>
              Saved. The {noun} follows this
              {confirmed ? ` ${scopePhrase(confirmed.proposal.scope, confirmed.proposal.scope.kind === 'property' ? propertyName(confirmed.proposal.scope.propertyId) : undefined)}` : ''} from its next step.
            </span>
          </div>
          <p className="m-0 text-sm font-semibold text-foreground" data-testid="improvement-outcome-rule">
            {confirmed ? proposalRuleText(confirmed.proposal) : lastAgentText}
          </p>
          <Link
            to={`/settings/agents/${view.anchor.agentKind}/playbook`}
            data-testid="improvement-see-rules"
            className="flex min-h-[44px] items-center text-sm text-primary hover:underline"
          >
            See it in your rules
          </Link>
        </>
      )}
      {outcome === 'defect_escalated' && (
        <>
          <div
            role="status"
            data-testid="improvement-outcome-banner"
            className="rounded-lg border border-info/25 bg-info/10 p-3 text-sm text-info"
          >
            Reported to the Resida team with this conversation. Your rules haven't changed.
          </div>
          {lastAgentText && <p className="m-0 text-sm leading-normal text-foreground">{lastAgentText}</p>}
        </>
      )}
      {outcome === 'explained' && (
        <>
          <p className="m-0 text-sm leading-normal text-foreground" data-testid="improvement-outcome-text">
            {lastAgentText}
          </p>
          <div>
            <Button type="button" className="font-semibold" onClick={onDone} data-testid="improvement-makes-sense">
              That makes sense
            </Button>
          </div>
        </>
      )}
      {outcome === 'declined' && (
        <>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4" aria-hidden="true" />
            <span className="text-sm font-semibold text-foreground">Always applies</span>
          </div>
          <p className="m-0 text-sm leading-normal text-foreground" data-testid="improvement-outcome-text">
            {lastAgentText}
          </p>
        </>
      )}
      {outcome === 'abandoned' && (
        <p className="m-0 text-sm text-muted-foreground" data-testid="improvement-outcome-text">
          This conversation was closed without a change. Your rules haven't changed.
        </p>
      )}
      {rerouteButton}
    </section>
  );
}

export default ConversationOutcome;
